import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchGoddsList } from "../actions/cottageActions";
import PaginationControls from "./PaginationControls";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import Select from "./ui/Select";
import HotToastMessage from "./ui/HotToastMessage";
import ConfirmModal from "./ui/ConfirmModal";
import { Search, Trash2, FileSpreadsheet, X } from "lucide-react";

const GoodsList = ({ storageOnly = false }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    goods = [],
    loading,
    error,
    next,
    previous,
    count,
  } = useSelector((state) => state.cottageGoods);

  // Local state
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");

  const [cottageFilter, setCottageFilter] = useState("");
  const [proformaFilter, setProformaFilter] = useState("");
  const [cottageQuery, setCottageQuery] = useState("");
  const [proformaQuery, setProformaQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [cottageNumbers, setCottageNumbers] = useState([]);
  const [orderNumbers, setOrderNumbers] = useState([]);

  const [selectedGoods, setSelectedGoods] = useState([]);
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [showModal, setShowModal] = useState(false);

  // Derived
  const totalPages = count ? Math.ceil(count / pageSize) : 1;
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const areAllSelected =
    goods.length > 0 && selectedGoods.length === goods.length;

  // React-select styles (consistent with CottageList)
  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        background: "rgba(255,255,255,0.98)",
        borderColor: "#E5E7EB",
        borderRadius: "1rem",
        padding: "0.2rem",
        boxShadow: "none",
        textAlign: "right",
        minHeight: "44px",
        ":hover": { borderColor: "#2563EB" },
      }),
      menu: (base) => ({ ...base, textAlign: "right", zIndex: 60 }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "#2563EB"
          : state.isFocused
          ? "#DBEAFE"
          : "#fff",
        color: state.isSelected ? "#fff" : "#111827",
      }),
      singleValue: (base) => ({ ...base, textAlign: "right" }),
      placeholder: (base) => ({ ...base, textAlign: "right" }),
    }),
    []
  );

  // Options
  const cottageOptions = useMemo(
    () => [
      { value: "", label: "همه کوتاژها" },
      ...cottageNumbers.map((n) => ({ value: String(n), label: String(n) })),
    ],
    [cottageNumbers]
  );

  const proformaOptions = useMemo(
    () => [
      { value: "", label: "همه ثبت سفارش‌ها" },
      ...orderNumbers.map((n) => ({ value: String(n), label: String(n) })),
    ],
    [orderNumbers]
  );

  const activeFiltersCount = useMemo(
    () => [query, cottageQuery, proformaQuery].filter(Boolean).length,
    [query, cottageQuery, proformaQuery]
  );

  const filterChips = useMemo(() => {
    const chips = [];
    if (query) chips.push({ k: "جستجو", v: query });
    if (cottageQuery) chips.push({ k: "کوتاژ", v: cottageQuery });
    if (proformaQuery) chips.push({ k: "ثبت سفارش", v: proformaQuery });
    if (storageOnly) chips.push({ k: "بخش", v: "انبار" });
    return chips;
  }, [query, cottageQuery, proformaQuery, storageOnly]);

  // Fetch proforma numbers
  useEffect(() => {
    const fetchPerformaNumbers = async () => {
      try {
        const res = await axiosInstance.get("/performas/numbers");
        if (res.data && Array.isArray(res.data)) {
          setOrderNumbers(res.data.map((i) => i.prf_order_no).filter(Boolean));
        } else {
          setOrderNumbers([]);
        }
      } catch {
        setOrderNumbers([]);
      }
    };
    fetchPerformaNumbers();
  }, []);

  // Fetch cottage numbers
  useEffect(() => {
    const fetchCottageNumbers = async () => {
      try {
        const res = await axiosInstance.get("/cottages/numbers/");
        if (res.data?.results && Array.isArray(res.data.results)) {
          setCottageNumbers(
            res.data.results.map((i) => i.cottage_number).filter(Boolean)
          );
        } else {
          setCottageNumbers([]);
        }
      } catch {
        setCottageNumbers([]);
      }
    };
    fetchCottageNumbers();
  }, []);

  // Debounce filters/search
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchText.trim());
      setCottageQuery(cottageFilter || "");
      setProformaQuery(proformaFilter || "");
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText, cottageFilter, proformaFilter]);

  // Build filters object (single source of truth)
  const buildGoodsFilters = useCallback(() => ({
    search: query,
    cottage: cottageQuery,
    proforma: proformaQuery,
    ...(storageOnly && { cottage_exited: "True" }),
  }), [query, cottageQuery, proformaQuery, storageOnly]);

  // Fetch goods
  useEffect(() => {
    dispatch(fetchGoddsList(currentPage, pageSize, buildGoodsFilters()));
  }, [dispatch, currentPage, pageSize, buildGoodsFilters]);

  // Selection
  const handleSelectGood = (e, good) => {
    const checked = e.target.checked;
    setSelectedGoods((prev) => {
      if (checked) return [...new Set([...prev, good.goodscode])];
      return prev.filter((id) => id !== good.goodscode);
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) setSelectedGoods(goods.map((g) => g.goodscode));
    else setSelectedGoods([]);
  };

  const clearFilters = () => {
    setSearchText("");
    setCottageFilter("");
    setProformaFilter("");
  };

  // Download helper
  const downloadExcelBlob = (blobData, filename) => {
    const blob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Export selected goods (existing behavior)
  const handleExportSelected = async () => {
    if (!selectedGoods.length) {
      setToast({
        message: "لطفاً حداقل یک کالا را انتخاب کنید.",
        type: "warn",
      });
      return;
    }

    try {
      const res = await axiosInstance.post(
        "/export-cottage-goods-excel/",
        { goods_codes: selectedGoods },
        { responseType: "blob" }
      );

      downloadExcelBlob(
        res.data,
        `goods_selected_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      setToast({ message: "فایل اکسل با موفقیت صادر شد.", type: "success" });
    } catch (err) {
      console.error("Export selected error:", err);
      setToast({ message: "خطا در صدور فایل اکسل.", type: "error" });
    }
  };

  // NEW: Export ALL goods matching current filters (ignores pagination)
  const handleExportFiltered = async () => {
    try {
      const filters = buildGoodsFilters();

      const res = await axiosInstance.post(
        "/export-cottage-goods-excel/",
        {
          export_all_filtered: true,
          filters, // {search, cottage, proforma}
        },
        { responseType: "blob" }
      );

      downloadExcelBlob(
        res.data,
        `goods_filtered_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      setToast({
        message: "اکسلِ فیلتر شده با موفقیت صادر شد.",
        type: "success",
      });
    } catch (err) {
      console.error("Export filtered error:", err);
      setToast({ message: "خطا در صدور اکسلِ فیلتر شده.", type: "error" });
    }
  };

  // Delete flow (placeholder)
  const handleDeleteSelectedGoods = () => {
    if (!selectedGoods.length) {
      setToast({
        message: "لطفاً حداقل یک کالا را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowModal(false);

    try {
      // TODO: wire your delete endpoint here
      setToast({
        message: "کالاهای انتخاب شده با موفقیت حذف شدند.",
        type: "success",
      });
      setSelectedGoods([]);
    } catch (err) {
      console.error("Delete error:", err);
      setToast({ message: "خطا در حذف کالاها.", type: "error" });
    }
  };

  const handleCancelDelete = () => setShowModal(false);

  // Pagination
  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Helpers
  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
    // note: returns null for NaN/inf
  };

  const unitPrice = (finalPrice, qty) => {
    const fp = safeNum(finalPrice);
    const q = safeNum(qty);
    if (!fp || !q || q === 0) return "—";
    return Math.round(fp / q).toLocaleString("en-US");
  };

  return (
    <div className="mt-24 pb-24 bg-slate-50 min-h-screen font-vazir" dir="rtl">
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />

      <ConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="mx-auto max-w-7xl md:max-w-[90%] px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-white p-5 sm:p-6 shadow-sm ring-1 ring-slate-200">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="text-right">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                {storageOnly ? "انبار" : "لیست کالاها"}
              </h2>
              {storageOnly && (
                <p className="mt-2 text-sm text-slate-500">
                  کالاهای اظهارنامه‌هایی که خروج شده هستند
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  تعداد: {count ?? 0}
                </span>
                {activeFiltersCount > 0 ? (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">
                    {activeFiltersCount} فیلتر فعال
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    بدون فیلتر
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm ring-1 ring-slate-200 hover:bg-slate-50 transition"
                >
                  <X className="w-4 h-4" />
                  پاک‌سازی
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 text-right mb-2">
              جستجو (کد کالا / HS Code / شرح کالا):
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="جستجوی خود را وارد کنید…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            </div>

            {filterChips.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {filterChips.map((c, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100"
                  >
                    <b className="font-medium">{c.k}:</b> {c.v}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                شماره کوتاژ:
              </label>
              <Select
                value={
                  cottageOptions.find(
                    (o) => o.value === String(cottageFilter)
                  ) || null
                }
                onChange={(opt) => setCottageFilter(opt ? opt.value : "")}
                options={cottageOptions}
                placeholder="انتخاب شماره کوتاژ"
                isClearable
                styles={selectStyles}
                className="text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                ثبت سفارش:
              </label>
              <Select
                value={
                  proformaOptions.find(
                    (o) => o.value === String(proformaFilter)
                  ) || null
                }
                onChange={(opt) => setProformaFilter(opt ? opt.value : "")}
                options={proformaOptions}
                placeholder="انتخاب ثبت سفارش"
                isClearable
                styles={selectStyles}
                className="text-sm"
              />
            </div>
          </div>

          {/* Top actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={handleExportSelected}
              disabled={!selectedGoods.length}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className="w-4 h-4" />
              اکسل انتخابی‌ها
            </button>

            <button
              onClick={handleExportFiltered}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition ring-1 ring-emerald-200"
            >
              <FileSpreadsheet className="w-4 h-4" />
              اکسلِ فیلتر شده
            </button>

            <button
              onClick={handleDeleteSelectedGoods}
              disabled={!selectedGoods.length}
              className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              حذف انتخابی‌ها
            </button>
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="mt-8 grid gap-3">
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
            </div>
          )}

          {error && !loading && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          {/* Table & Pagination */}
          {!loading && !error && (
            <>
              {goods.length === 0 ? (
                <div className="mt-10 text-center text-slate-600">
                  هیچ کالایی یافت نشد.
                </div>
              ) : (
                <>
                  <div className="mt-5">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200, 500]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>

                  <div className="mt-3 overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-right text-slate-700">
                            <th className="p-3">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={areAllSelected}
                              />
                            </th>
                            <th className="p-3 font-semibold">ردیف</th>
                            <th className="p-3 font-semibold">کد کالا</th>
                            <th className="p-3 font-semibold">HS Code</th>
                            <th className="p-3 font-semibold">انبار</th>
                            <th className="p-3 font-semibold">ثبت سفارش</th>
                            <th className="p-3 font-semibold">شماره کوتاژ</th>
                            <th className="p-3 font-semibold">شرح کالا</th>
                            <th className="p-3 font-semibold">تعداد</th>
                            <th className="p-3 font-semibold">وزن خالص</th>
                            <th className="p-3 font-semibold">وزن ناخالص</th>
                            {storageOnly ? (
                              <th className="p-3 font-semibold">وضعیت فروش</th>
                            ) : null}
                            <th className="p-3 font-semibold">ارزش کل ارزی</th>
                            {!storageOnly ? (
                              <>
                                <th className="p-3 font-semibold">حواله ریالی</th>
                                <th className="p-3 font-semibold">قیمت نهایی</th>
                                <th className="p-3 font-semibold">قیمت واحد</th>
                              </>
                            ) : null}
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                          {goods.map((good, index) => {
                            const proforma = good?.cottage?.proforma;
                            const cottageNumber = good?.cottage?.cottage_number;

                            return (
                              <tr
                                key={good.id}
                                className="hover:bg-slate-50 transition"
                              >
                                <td className="p-3 align-middle">
                                  <input
                                    type="checkbox"
                                    checked={selectedGoods.includes(
                                      good.goodscode
                                    )}
                                    onChange={(e) => handleSelectGood(e, good)}
                                  />
                                </td>

                                <td className="p-3 align-middle">
                                  {(currentPage - 1) * pageSize + (index + 1)}
                                </td>

                                <td className="p-3 align-middle font-medium text-slate-900">
                                  {storageOnly ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/storage/goods/${good.id}`}
                                    >
                                      {good.goodscode}
                                    </Link>
                                  ) : (
                                    good.goodscode
                                  )}
                                </td>

                                <td className="p-3 align-middle font-medium text-slate-900">
                                  {good.HSCode || "—"}
                                </td>

                                <td className="p-3 align-middle min-w-[220px]">
                                  {good.storage_detail ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/storages/${good.storage_detail.id}`}
                                    >
                                      {good.storage_detail.storage_name}
                                    </Link>
                                  ) : (
                                    <span className="text-slate-500">تعریف نشده</span>
                                  )}
                                </td>

                                <td className="p-3 align-middle">
                                  {good?.cottage?.is_patteh ? (
                                    "پته"
                                  ) : proforma?.prf_order_no ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/order-details/${proforma.prfVCodeInt}`}
                                    >
                                      {proforma.prf_order_no}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottageNumber ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/cottages/${cottageNumber}`}
                                    >
                                      {cottageNumber}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                <td className="p-3 align-middle max-w-[380px]">
                                  <div className="line-clamp-2 text-slate-800">
                                    {good.goods_description || "—"}
                                  </div>
                                </td>

                                <td className="p-3 align-middle">
                                  {good.quantity ?? "—"}
                                </td>
                                <td className="p-3 align-middle">
                                  {good.netweight ?? "—"}
                                </td>
                                <td className="p-3 align-middle">
                                  {good.grossweight ?? "—"}
                                </td>
                                {storageOnly ? (
                                  <td className="p-3 align-middle">
                                    {good.is_sold ? (
                                      good.sold_to_customer_detail ? (
                                        <Link
                                          className="text-blue-700 hover:underline"
                                          to={`/customers/details/${good.sold_to_customer_detail.id}`}
                                        >
                                          فروخته‌شده به {good.sold_to_customer_detail.full_name}
                                        </Link>
                                      ) : (
                                        "فروخته‌شده"
                                      )
                                    ) : (
                                      <span className="text-slate-500">فروخته‌نشده</span>
                                    )}
                                  </td>
                                ) : null}
                                <td className="p-3 align-middle">
                                  {good.total_value ?? "—"}
                                </td>
                                {!storageOnly ? (
                                  <>
                                    <td className="p-3 align-middle">
                                      {good.riali ?? "—"}
                                    </td>
                                    <td className="p-3 align-middle">
                                      {good.final_price ?? "—"}
                                    </td>
                                    <td className="p-3 align-middle">
                                      {unitPrice(good.final_price, good.quantity)}
                                    </td>
                                  </>
                                ) : null}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-3">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200, 500]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sticky bulk-actions bar */}
      {(selectedGoods.length > 0 || activeFiltersCount > 0) && (
        <div className="fixed left-0 right-0 bottom-4 z-20 px-4">
          <div className="mx-auto max-w-[1000px] rounded-3xl bg-white shadow-lg ring-1 ring-slate-200">
            <div className="p-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-slate-700">
                  {selectedGoods.length > 0 ? (
                    <>
                      <b>{selectedGoods.length}</b> مورد انتخاب شده
                    </>
                  ) : (
                    <>بدون انتخاب</>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExportFiltered}
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition ring-1 ring-emerald-200"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    اکسلِ فیلتر
                  </button>

                  <button
                    onClick={handleExportSelected}
                    disabled={!selectedGoods.length}
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    اکسل انتخابی
                  </button>

                  <button
                    onClick={handleDeleteSelectedGoods}
                    disabled={!selectedGoods.length}
                    className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsList;
